import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// ── Export cache (module-level, filled before CSV generation) ──────────────

let _assetCache: Map<string, string[]> | null = null; // partId → asset label[]

/** Returns asset labels for a part from the pre-loaded cache. */
export function getPartAssetLabels(partId: string): string[] {
  return _assetCache?.get(partId) ?? [];
}

/** Load part→asset links for all given part IDs before exporting. */
export async function loadAllPartAssets(partIds: string[]): Promise<void> {
  if (partIds.length === 0) return;

  const { data, error } = await supabase
    .from("part_asset")
    .select("part_id, asset:asset_id(name, parent:parent_id(name))")
    .in("part_id", partIds);

  if (error) throw error;

  _assetCache = new Map();
  for (const row of data ?? []) {
    const r = row as any;
    const parentName: string | null = r.asset?.parent?.name ?? null;
    const label: string = parentName ? `${parentName} › ${r.asset.name}` : r.asset.name;
    const existing = _assetCache.get(r.part_id) ?? [];
    existing.push(label);
    _assetCache.set(r.part_id, existing);
  }
}

export interface PartAssetLink {
  asset_id: string;
  asset_name: string;
  asset_type: string | null;
  image_url: string | null;
  parent_name: string | null;
}

/** Returns part IDs linked to a given asset — used for filtering the parts list. */
export function useAssetPartIds(assetId: string | undefined) {
  return useQuery({
    queryKey: ["asset-part-ids", assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_asset")
        .select("part_id")
        .eq("asset_id", assetId!);
      if (error) throw error;
      return new Set((data ?? []).map((r: any) => r.part_id as string));
    },
  });
}

export function usePartAssets(partId: string | undefined) {
  return useQuery({
    queryKey: ["part-assets", partId],
    enabled: !!partId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_asset")
        .select("asset_id, asset:asset_id(id, name, asset_type, image_url, parent:parent_id(name))")
        .eq("part_id", partId!);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        asset_id: row.asset_id,
        asset_name: row.asset?.name ?? "Unknown",
        asset_type: row.asset?.asset_type ?? null,
        image_url: row.asset?.image_url ?? null,
        parent_name: (row.asset?.parent as any)?.name ?? null,
      })) as PartAssetLink[];
    },
  });
}

export function useLinkPartAsset() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ partId, assetId }: { partId: string; assetId: string }) => {
      const { error } = await supabase.from("part_asset").insert({ part_id: partId, asset_id: assetId });
      if (error) throw error;
    },
    onSuccess: (_, { partId }) => {
      qc.invalidateQueries({ queryKey: ["part-assets", partId] });
    },
    onError: () => toast({ title: "Failed to link asset", variant: "destructive" }),
  });
}

export function useUnlinkPartAsset() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ partId, assetId }: { partId: string; assetId: string }) => {
      const { error } = await supabase.from("part_asset").delete().eq("part_id", partId).eq("asset_id", assetId);
      if (error) throw error;
    },
    onSuccess: (_, { partId }) => {
      qc.invalidateQueries({ queryKey: ["part-assets", partId] });
    },
    onError: () => toast({ title: "Failed to unlink asset", variant: "destructive" }),
  });
}
